import React, { useState, useEffect } from "react";
import {
  GiftType,
  SeaObjectType,
  ActivationCode,
  ActivationHistoryEntry,
} from "../types/treasure-hunt";
import {
  ROTATABLE_OBJECTS,
  GRID_COLS,
  INTERACTIVE_OBJECTS,
  AGE_OBJECTS,
} from "../constants/treasure-hunt";
import { GameField } from "../components/treasure-hunt/GameField";
import { SeaObjectsPanel } from "../components/treasure-hunt/SeaObjectsPanel";
import { GiftTextModal } from "../components/treasure-hunt/GiftTextModal";
import { Modal } from "../components/UI/Modal";
import { useTreasureHunt } from "../contexts/TreasureHuntContext";
import { treasureHuntApi } from "../services/treasureHunt.api";
import { useNotification } from "../contexts/NotificationContext";

export const GenerationPage = () => {
  const { tileMap, setTileMap } = useTreasureHunt();
  const { success, error } = useNotification();
  const [panelPosition, setPanelPosition] = useState<"right" | "bottom">(
    "right"
  );
  const [mapExists, setMapExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [tileSize, setTileSize] = useState(35);

  const [selectedGiftType, setSelectedGiftType] = useState<GiftType | null>(
    null
  );
  const [selectedSeaObject, setSelectedSeaObject] =
    useState<SeaObjectType | null>(null);
  const [lastRotations, setLastRotations] = useState<
    Record<SeaObjectType, number>
  >({} as Record<SeaObjectType, number>);
  const [showTextModal, setShowTextModal] = useState(false);
  const [giftText, setGiftText] = useState("");
  const [showPromoCodeModal, setShowPromoCodeModal] = useState(false);
  const [showActivationHistoryModal, setShowActivationHistoryModal] =
    useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [savingPromoCode, setSavingPromoCode] = useState(false);
  const [activationCodes, setActivationCodes] = useState<ActivationCode[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [codeCount, setCodeCount] = useState(1);
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadPostText, setDownloadPostText] = useState("");
  const [undownloadedCodes, setUndownloadedCodes] = useState<ActivationCode[]>([]);
  const [loadingUndownloaded, setLoadingUndownloaded] = useState(false);
  const [pendingPosition, setPendingPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [hoveredTile, setHoveredTile] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [tooltipMessage, setTooltipMessage] = useState<string | null>(null);
  const [tooltipAge, setTooltipAge] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const [showObjectTextModal, setShowObjectTextModal] = useState(false);
  const [objectMessageText, setObjectMessageText] = useState("");
  const [objectAge, setObjectAge] = useState<number | "">("");
  const [pendingObjectPosition, setPendingObjectPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [editingMode, setEditingMode] = useState(false);
  const [activationHistory, setActivationHistory] = useState<
    ActivationHistoryEntry[]
  >([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showClouds, setShowClouds] = useState(true); // Показывать ли слой облаков
  const [cloudEditMode, setCloudEditMode] = useState(false); // Режим редактирования облаков

  const handleTileClick = (x: number, y: number) => {
    // Режим редактирования облаков - переключаем наличие облаков на клетке
    if (cloudEditMode) {
      const newMap = tileMap.map((row) => row.map((t) => ({ ...t })));
      newMap[y][x] = {
        ...newMap[y][x],
        hasClouds: !newMap[y][x].hasClouds,
      };
      setTileMap(newMap);
      return;
    }

    // Специальные инструменты для работы с клетками
    if (selectedSeaObject === SeaObjectType.WATER) {
      // Карточка воды - очищаем клетку (превращаем в воду)
      const newMap = tileMap.map((row) => row.map((t) => ({ ...t })));
      newMap[y][x] = { tileCode: SeaObjectType.WATER };
      setTileMap(newMap);
      return;
    } else if (selectedSeaObject === SeaObjectType.SAND_ISLAND) {
      // Песчаный остров - меняем тип клетки на SAND (создаем пляж)
      const newMap = tileMap.map((row) => row.map((t) => ({ ...t })));
      newMap[y][x] = { tileCode: SeaObjectType.SAND_ISLAND };
      setTileMap(newMap);
      return;
    }

    // Размещение подарков
    if (selectedGiftType) {
      setPendingPosition({ x, y });
      setShowTextModal(true);
      return;
    }

    // Размещение объектов
    if (selectedSeaObject) {
      // В режиме генерации нет ограничений - админ может размещать любые объекты на любых клетках

      // Проверяем, требует ли объект текст послания
      if (INTERACTIVE_OBJECTS.includes(selectedSeaObject)) {
        setPendingObjectPosition({ x, y });
        setObjectMessageText("");
        // Если это человек (мужчина или женщина), очищаем поле возраста
        if (AGE_OBJECTS.includes(selectedSeaObject)) {
          setObjectAge("");
        }
        setShowObjectTextModal(true);
      } else {
        // Добавляем морской предмет без текста
        const newMap = tileMap.map((row) => row.map((t) => ({ ...t })));
        const savedRotation = lastRotations[selectedSeaObject] || 0;
        newMap[y][x] = {
          ...newMap[y][x],
          seaObject: {
            type: selectedSeaObject,
            rotation: savedRotation,
          },
        };
        setTileMap(newMap);
      }
    }
  };

  const handleSaveGift = () => {
    if (!pendingPosition || !selectedGiftType) return;

    const { x, y } = pendingPosition;
    const currentTile = tileMap[y][x];

    // Обновляем карту: добавляем подарок
    const newMap = tileMap.map((row) => row.map((tile) => ({ ...tile })));
    newMap[y][x] = {
      tileCode: currentTile.tileCode || SeaObjectType.SAND_ISLAND,
      gift: {
        type: selectedGiftType,
        text: giftText,
      },
    };

    setTileMap(newMap);
    setShowTextModal(false);
    setGiftText("");
    setPendingPosition(null);
  };

  const handleCancelModal = () => {
    setShowTextModal(false);
    setGiftText("");
    setPendingPosition(null);
  };

  const handleSaveObjectWithText = () => {
    if (!pendingObjectPosition || !selectedSeaObject) return;

    const { x, y } = pendingObjectPosition;
    const newMap = tileMap.map((row) => row.map((tile) => ({ ...tile })));
    const savedRotation = lastRotations[selectedSeaObject] || 0;

    // Проверяем, является ли объект человеком (требуется возраст)
    const isAgeObject = AGE_OBJECTS.includes(selectedSeaObject);

    newMap[y][x] = {
      ...newMap[y][x],
      seaObject: {
        type: selectedSeaObject,
        rotation: savedRotation,
        text: objectMessageText,
        // Добавляем возраст только если это человек и возраст указан
        ...(isAgeObject && objectAge !== "" && { age: Number(objectAge) }),
      },
    };

    setTileMap(newMap);
    setShowObjectTextModal(false);
    setObjectMessageText("");
    setObjectAge("");
    setPendingObjectPosition(null);
  };

  const handleCancelObjectModal = () => {
    setShowObjectTextModal(false);
    setObjectMessageText("");
    setObjectAge("");
    setPendingObjectPosition(null);
    setEditingMode(false);
  };

  const handleTileDoubleClick = (x: number, y: number) => {
    const tile = tileMap[y][x];
    // Если на тайле есть интерактивный морской предмет, открываем модальное окно для редактирования
    if (tile.seaObject && INTERACTIVE_OBJECTS.includes(tile.seaObject.type)) {
      setPendingObjectPosition({ x, y });
      setObjectMessageText(tile.seaObject.text || "");
      // Загружаем возраст, если это человек
      if (AGE_OBJECTS.includes(tile.seaObject.type)) {
        setObjectAge(tile.seaObject.age || "");
      }
      setEditingMode(true);
      setShowObjectTextModal(true);
    }
  };

  const handleRotateSeaObject = (x: number, y: number) => {
    const tile = tileMap[y][x];
    if (tile.seaObject && ROTATABLE_OBJECTS.includes(tile.seaObject.type)) {
      const newMap = tileMap.map((row) => row.map((t) => ({ ...t })));
      const currentRotation = newMap[y][x].seaObject!.rotation;
      const newRotation = (currentRotation + 90) % 360;
      newMap[y][x] = {
        ...newMap[y][x],
        seaObject: {
          ...newMap[y][x].seaObject!,
          rotation: newRotation,
        },
      };
      setTileMap(newMap);

      // Сохраняем угол поворота для этого типа объекта
      setLastRotations((prev) => ({
        ...prev,
        [tile.seaObject!.type]: newRotation,
      }));
    }
  };

  // Вычисление размера клеток при изменении размера окна
  useEffect(() => {
    const calculateTileSize = () => {
      const windowWidth = window.innerWidth;

      // Если экран меньше 1800px - оставляем клетки 35px для скроллинга
      // На планшетах и смартфонах клетки останутся различимыми
      if (windowWidth < 1800) {
        setTileSize(35);
        return;
      }

      // На больших экранах растягиваем карту на всю ширину
      const tileSizeByWidth = windowWidth / GRID_COLS;
      setTileSize(tileSizeByWidth);
    };

    calculateTileSize();
    window.addEventListener("resize", calculateTileSize);

    return () => window.removeEventListener("resize", calculateTileSize);
  }, []);

  // Автоматическая загрузка карты при монтировании компонента
  useEffect(() => {
    const loadMap = async () => {
      setIsMapLoading(true);
      try {
        const response = await treasureHuntApi.getMap();
        if (response.success && response.data) {
          setTileMap(response.data);
          setMapExists(true);
        } else {
          setMapExists(false);
        }
      } catch (err) {
        console.error("Карта не найдена, используется пустая карта");
        setMapExists(false);
      } finally {
        setIsMapLoading(false);
      }
    };

    loadMap();
  }, [setTileMap]);

  // Сохранить карту
  const handleSaveMap = async () => {
    setLoading(true);
    try {
      await treasureHuntApi.saveMap(tileMap);
      setMapExists(true);
      success("Успешно", "Карта сохранена");
    } catch (err) {
      error("Ошибка", "Не удалось сохранить карту");
    } finally {
      setLoading(false);
    }
  };

  // Перезагрузить карту с сервера
  const handleLoadMap = async () => {
    if (
      !window.confirm(
        "Загрузить карту с сервера? Текущие несохраненные изменения будут потеряны."
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const response = await treasureHuntApi.getMap();
      if (response.success && response.data) {
        setTileMap(response.data);
        success("Успешно", "Карта перезагружена с сервера");
      } else {
        error("Ошибка", "Карта не найдена на сервере");
      }
    } catch (err) {
      error("Ошибка", "Не удалось загрузить карту");
    } finally {
      setLoading(false);
    }
  };

  // Сбросить карту
  const handleResetMap = async () => {
    if (
      !window.confirm(
        "Вы уверены, что хотите сбросить карту? Это действие необратимо."
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await treasureHuntApi.resetMap();
      setMapExists(false);
      success("Успешно", "Карта сброшена");
    } catch (err) {
      error("Ошибка", "Не удалось сбросить карту");
    } finally {
      setLoading(false);
    }
  };

  // Генерация кода активации
  const generatePromoCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPromoCode(code);
  };

  // Загрузка кодов активации
  const loadActivationCodes = async () => {
    setLoadingCodes(true);
    try {
      const response = await treasureHuntApi.getAllActivationCodes();
      if (response.success && response.data) {
        setActivationCodes(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error("Ошибка загрузки кодов активации", err);
      setActivationCodes([]);
    } finally {
      setLoadingCodes(false);
    }
  };

  // Загрузка истории активаций
  const loadActivationHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await treasureHuntApi.getAllActivationHistory();
      if (response.success && response.data) {
        setActivationHistory(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error("Ошибка загрузки истории активаций", err);
      setActivationHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Сохранение кода активации
  const handleSavePromoCode = async () => {
    if (!promoCode.trim()) {
      error("Ошибка", "Введите код активации");
      return;
    }

    setSavingPromoCode(true);
    try {
      const response = await treasureHuntApi.createActivationCode(
        promoCode.trim()
      );
      if (response.success) {
        success("Успешно", `Код активации "${promoCode}" сохранен`);
        setPromoCode("");
        await loadActivationCodes();
      } else {
        error(
          "Ошибка",
          response.message || "Не удалось сохранить код активации"
        );
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Не удалось сохранить код активации";
      error("Ошибка", errorMessage);
    } finally {
      setSavingPromoCode(false);
    }
  };

  // Массовая генерация кодов
  const handleGenerateMultipleCodes = async () => {
    if (codeCount < 1 || codeCount > 100) {
      error("Ошибка", "Количество кодов должно быть от 1 до 100");
      return;
    }

    setGeneratingCodes(true);
    try {
      const response = await treasureHuntApi.generateMultipleCodes(codeCount);
      if (response.success) {
        success("Успешно", `Сгенерировано кодов: ${codeCount}`);
        await loadActivationCodes();
        setCodeCount(1);
      } else {
        error("Ошибка", response.message || "Не удалось сгенерировать коды");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Не удалось сгенерировать коды";
      error("Ошибка", errorMessage);
    } finally {
      setGeneratingCodes(false);
    }
  };

  // Загрузка невыгруженных кодов
  const loadUndownloadedCodes = async () => {
    setLoadingUndownloaded(true);
    try {
      const response = await treasureHuntApi.getUndownloadedActivationCodes();
      if (response.success && response.data) {
        setUndownloadedCodes(Array.isArray(response.data) ? response.data : []);
      }
    } catch (err) {
      console.error("Ошибка загрузки невыгруженных кодов", err);
      setUndownloadedCodes([]);
    } finally {
      setLoadingUndownloaded(false);
    }
  };

  // Открытие модального окна выгрузки
  const handleOpenDownloadModal = async () => {
    // Сначала мигрируем существующие коды (на случай если они были созданы до добавления поля isDownloaded)
    try {
      await treasureHuntApi.migrateExistingCodes();
    } catch (err) {
      console.log("Миграция не требуется или уже выполнена");
    }
    await loadUndownloadedCodes();
    setShowDownloadModal(true);
  };

  // Копирование всех кодов с пост-текстом
  const handleCopyCodesWithPostText = () => {
    const text = undownloadedCodes
      .map((code) => `${code.code}${downloadPostText ? ' ' + downloadPostText : ''}`)
      .join('\n');

    navigator.clipboard.writeText(text);
    success("Скопировано", "Коды скопированы в буфер обмена");
  };

  // Пометить коды как выгруженные
  const handleMarkAsDownloaded = async () => {
    if (undownloadedCodes.length === 0) {
      error("Ошибка", "Нет кодов для выгрузки");
      return;
    }

    try {
      const codeIds = undownloadedCodes.map((code) => code.id);
      const response = await treasureHuntApi.markCodesAsDownloaded(codeIds);
      if (response.success) {
        success("Успешно", "Коды отмечены как выгруженные");
        setShowDownloadModal(false);
        setDownloadPostText("");
        await loadActivationCodes();
      } else {
        error("Ошибка", response.message || "Не удалось отметить коды");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Не удалось отметить коды";
      error("Ошибка", errorMessage);
    }
  };

  // Загрузка кодов при открытии модального окна
  useEffect(() => {
    if (showPromoCodeModal) {
      loadActivationCodes();
    }
  }, [showPromoCodeModal]);

  // Загрузка истории активаций при открытии модального окна
  useEffect(() => {
    if (showActivationHistoryModal) {
      loadActivationHistory();
    }
  }, [showActivationHistoryModal]);

  // Покрыть всю карту облаками
  const handleCoverAllWithClouds = () => {
    const newMap = tileMap.map((row) =>
      row.map((tile) => ({
        ...tile,
        hasClouds: true,
      }))
    );
    setTileMap(newMap);
    success("Успешно", "Вся карта покрыта облаками");
  };

  // Убрать все облака с карты
  const handleRemoveAllClouds = () => {
    const newMap = tileMap.map((row) =>
      row.map((tile) => ({
        ...tile,
        hasClouds: false,
      }))
    );
    setTileMap(newMap);
    success("Успешно", "Все облака убраны с карты");
  };

  // Автоскролл при приближении к краям видимой области карты (как в стратегиях)
  useEffect(() => {
    const EDGE_THRESHOLD = 50; // Расстояние от края в пикселях
    const SCROLL_SPEED = 10; // Скорость скролла
    let scrollInterval: NodeJS.Timeout | null = null;
    let currentScrollX = 0;
    let currentScrollY = 0;

    const handleMouseMove = (e: MouseEvent) => {
      const container = document.querySelector('.map-scroll-container');
      if (!container) return;

      const rect = container.getBoundingClientRect();

      // Позиция курсора относительно viewport (окна браузера)
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      // Границы видимой области контейнера
      const containerLeft = rect.left;
      const containerRight = rect.right;
      const containerTop = rect.top;
      const containerBottom = rect.bottom;

      let scrollX = 0;
      let scrollY = 0;

      // Проверяем, находится ли курсор внутри контейнера
      if (mouseX >= containerLeft && mouseX <= containerRight &&
          mouseY >= containerTop && mouseY <= containerBottom) {

        // Проверка левого края видимой области
        if (mouseX - containerLeft < EDGE_THRESHOLD) {
          scrollX = -SCROLL_SPEED;
        }
        // Проверка правого края видимой области
        else if (containerRight - mouseX < EDGE_THRESHOLD) {
          scrollX = SCROLL_SPEED;
        }

        // Проверка верхнего края видимой области
        if (mouseY - containerTop < EDGE_THRESHOLD) {
          scrollY = -SCROLL_SPEED;
        }
        // Проверка нижнего края видимой области
        else if (containerBottom - mouseY < EDGE_THRESHOLD) {
          scrollY = SCROLL_SPEED;
        }
      }

      // Обновляем текущие значения скролла
      currentScrollX = scrollX;
      currentScrollY = scrollY;

      // Если нужно скроллить
      if (scrollX !== 0 || scrollY !== 0) {
        if (!scrollInterval) {
          scrollInterval = setInterval(() => {
            container.scrollLeft += currentScrollX;
            container.scrollTop += currentScrollY;
          }, 16); // ~60fps
        }
      } else {
        // Останавливаем скролл
        if (scrollInterval) {
          clearInterval(scrollInterval);
          scrollInterval = null;
        }
      }
    };

    const stopScroll = () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
        scrollInterval = null;
      }
      currentScrollX = 0;
      currentScrollY = 0;
    };

    document.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('blur', stopScroll);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('blur', stopScroll);
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, []);

  return (
    <div
      className="flex items-center justify-center w-screen h-screen relative overflow-hidden"
      style={{
        backgroundImage: "url(/water.gif)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Затемняющий оверлей */}
      <div className="absolute inset-0  z-0" />

      {/* Индикатор загрузки карты */}
      {isMapLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl px-8 py-6 flex flex-col items-center">
            <p className="text-gray-800 font-bold text-lg">
              Загрузка карты...
            </p>
          </div>
        </div>
      )}

      <div className="relative z-10 w-full h-full flex flex-col" style={{ visibility: isMapLoading ? 'hidden' : 'visible' }}>
        {/* Кнопки управления сверху карты */}
        <div className="flex flex-col items-center gap-3 py-4">
          {/* Кнопки кодов активации и истории */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowPromoCodeModal(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg shadow-lg transition-all hover:scale-105"
            >
              🎟️ Коды активации
            </button>
            <button
              onClick={() => setShowActivationHistoryModal(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow-lg transition-all hover:scale-105"
            >
              📜 История активаций
            </button>
          </div>

          {/* Кнопки управления облаками */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowClouds(!showClouds);
                if (cloudEditMode) setCloudEditMode(false);
              }}
              className={`px-4 py-2 ${
                showClouds
                  ? "bg-sky-600 hover:bg-sky-700"
                  : "bg-gray-600 hover:bg-gray-700"
              } text-white font-semibold rounded-lg shadow-lg transition-all hover:scale-105`}
            >
              {showClouds ? "☁️ Скрыть облака" : "☁️ Показать облака"}
            </button>
            <button
              onClick={() => {
                setCloudEditMode(!cloudEditMode);
                if (!cloudEditMode) {
                  setSelectedSeaObject(null);
                  setSelectedGiftType(null);
                }
              }}
              className={`px-4 py-2 ${
                cloudEditMode
                  ? "bg-orange-600 hover:bg-orange-700"
                  : "bg-cyan-600 hover:bg-cyan-700"
              } text-white font-semibold rounded-lg shadow-lg transition-all hover:scale-105`}
            >
              {cloudEditMode ? "✋ Выйти из режима" : "✏️ Редактировать облака"}
            </button>
            <button
              onClick={handleCoverAllWithClouds}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg transition-all hover:scale-105"
            >
              ☁️ Покрыть все
            </button>
            <button
              onClick={handleRemoveAllClouds}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white font-semibold rounded-lg shadow-lg transition-all hover:scale-105"
            >
              🌤️ Убрать все
            </button>
          </div>

          {/* Кнопки управления картой */}
          <div className="flex gap-2">
            <button
              onClick={handleSaveMap}
              disabled={loading}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Загрузка..." : "💾 Сохранить"}
            </button>

            {mapExists && (
              <>
                <button
                  onClick={handleLoadMap}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  🔄 Перезагрузить
                </button>

                <button
                  onClick={handleResetMap}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  🗑️ Сбросить
                </button>
              </>
            )}
          </div>
        </div>
        {/* Подсказка режима редактирования облаков */}
        {cloudEditMode && (
          <div className="text-center py-2 bg-orange-500 text-white font-semibold">
            🖱️ Режим редактирования облаков: Нажимайте на клетки, чтобы добавить или убрать облака
          </div>
        )}
        {/* Подсказка о перемещении карты */}
        <div className="text-center py-2 bg-blue-600 text-white text-sm">
          💡 Подведите курсор к краю экрана для перемещения карты
        </div>
        {/* Контейнер с картой */}
        <div className="flex-1 overflow-auto map-scroll-container">
          <GameField
            tileMap={tileMap}
            hoveredTile={hoveredTile}
            onTileClick={handleTileClick}
            onTileDoubleClick={handleTileDoubleClick}
            showClouds={showClouds}
            cloudEditMode={cloudEditMode}
            onTileHover={(x: number, y: number, event?: React.MouseEvent) => {
              setHoveredTile({ x, y });
              const tile = tileMap[y]?.[x];
              if (
                tile?.seaObject &&
                INTERACTIVE_OBJECTS.includes(tile.seaObject.type)
              ) {
                // Устанавливаем текст послания, если есть
                setTooltipMessage(tile.seaObject.text || null);
                // Устанавливаем возраст, если это человек
                if (AGE_OBJECTS.includes(tile.seaObject.type)) {
                  setTooltipAge(tile.seaObject.age || null);
                } else {
                  setTooltipAge(null);
                }
                if (event) {
                  setTooltipPosition({ x: event.clientX, y: event.clientY });
                }
              } else {
                setTooltipMessage(null);
                setTooltipAge(null);
              }
            }}
            onTileLeave={() => {
              setHoveredTile(null);
              setTooltipMessage(null);
              setTooltipAge(null);
            }}
            onRotate={handleRotateSeaObject}
            tileSize={tileSize}
          />
        </div>
      </div>
      {/* Боковая панель с морскими предметами */}
      <SeaObjectsPanel
        selectedSeaObject={selectedSeaObject}
        onSelect={(objectType) => {
          setSelectedSeaObject(objectType);
          setSelectedGiftType(null);
        }}
        position={panelPosition}
        onPositionChange={setPanelPosition}
      />
      {/* Модальное окно для ввода текста */}
      <GiftTextModal
        isOpen={showTextModal}
        value={giftText}
        onChange={setGiftText}
        onSave={handleSaveGift}
        onCancel={handleCancelModal}
      />
      {/* Модальное окно кодов активации */}
      <Modal
        isOpen={showPromoCodeModal}
        onClose={() => {
          setShowPromoCodeModal(false);
          setPromoCode("");
        }}
        title="Коды активации"
        size="lg"
      >
        <div className="space-y-4">
          {/* Одиночная генерация кода */}
          <div className="flex gap-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="Введите код активации"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSavePromoCode();
                }
              }}
            />
            <button
              onClick={generatePromoCode}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all hover:scale-105 flex items-center justify-center"
              title="Сгенерировать код активации"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            <button
              onClick={handleSavePromoCode}
              disabled={savingPromoCode || !promoCode.trim()}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingPromoCode ? "Сохранение..." : "Сохранить"}
            </button>
          </div>

          {/* Массовая генерация кодов */}
          <div className="flex gap-2 items-center border-t pt-4">
            <label className="text-sm font-semibold text-gray-700">
              Массовая генерация:
            </label>
            <input
              type="number"
              value={codeCount}
              onChange={(e) => setCodeCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
              min="1"
              max="100"
              className="w-24 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleGenerateMultipleCodes}
              disabled={generatingCodes}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generatingCodes ? "Генерация..." : "Сгенерировать"}
            </button>
          </div>

          {/* Кнопка выгрузки кодов */}
          <div className="border-t pt-4">
            <button
              onClick={handleOpenDownloadModal}
              className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all hover:scale-105"
            >
              📥 Выгрузить коды
            </button>
          </div>

          {/* Список кодов активации */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3">
              Список кодов активации
            </h3>
            {loadingCodes ? (
              <div className="text-center py-4 text-gray-500">Загрузка...</div>
            ) : activationCodes.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                Коды активации отсутствуют
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Код
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Статус
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Дата создания
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {activationCodes.map((code) => (
                      <tr key={code.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm font-mono flex items-center gap-2">
                          {code.code}
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(code.code);
                              success(
                                "Скопировано",
                                `Код "${code.code}" скопирован в буфер обмена`
                              );
                            }}
                            className="ml-2 px-1 py-0.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-all text-xs"
                            title="Скопировать код в буфер"
                          >
                            📋
                          </button>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                code.isUsed
                                  ? "bg-red-100 text-red-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {code.isUsed ? "Использован" : "Не использован"}
                            </span>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                code.isDownloaded
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {code.isDownloaded ? "Выгружен" : "Не выгружен"}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {new Date(code.createdAt).toLocaleDateString(
                            "ru-RU",
                            {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Modal>
      {/* Модальное окно истории активаций */}
      <Modal
        isOpen={showActivationHistoryModal}
        onClose={() => setShowActivationHistoryModal(false)}
        title="История активаций"
        size="lg"
      >
        <div className="space-y-4">
          {loadingHistory ? (
            <div className="text-center py-4 text-gray-500">Загрузка...</div>
          ) : activationHistory.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              История активаций пуста
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Код
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Название
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Содержание
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Позиция
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Дата
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {activationHistory.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-mono">
                        {entry.code}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span className="truncate block max-w-xs">
                          {entry.messageTitle}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span className="truncate block max-w-xs">
                          {entry.messageContent}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        ({entry.tileData.x}, {entry.tileData.y})
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {new Date(entry.createdAt).toLocaleDateString("ru-RU", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
      {/* Модальное окно для ввода текста объекта */}
      {showObjectTextModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-white text-xl mb-4">
              {editingMode
                ? "Редактировать данные / Edit data:"
                : "Введите данные / Enter data:"}
            </h2>
            {selectedSeaObject && AGE_OBJECTS.includes(selectedSeaObject) && (
              <div className="mb-4">
                <label className="text-white text-sm mb-2 block">
                  Возраст / Age:
                </label>
                <input
                  type="number"
                  className="w-full p-3 rounded bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-blue-500"
                  value={objectAge}
                  onChange={(e) => setObjectAge(e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Введите возраст / Enter age"
                  min="1"
                  max="150"
                />
              </div>
            )}
            <div>
              <label className="text-white text-sm mb-2 block">
                Текст послания / Message text:
              </label>
              <textarea
                className="w-full p-3 rounded bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-blue-500"
                rows={4}
                value={objectMessageText}
                onChange={(e) => setObjectMessageText(e.target.value)}
                placeholder="Введите текст послания / Enter message text (optional)"
                autoFocus={!selectedSeaObject || !AGE_OBJECTS.includes(selectedSeaObject)}
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleSaveObjectWithText}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
              >
                Сохранить / Save
              </button>
              <button
                onClick={handleCancelObjectModal}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
              >
                Отмена / Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Всплывающая подсказка с текстом послания и возрастом */}
      {(tooltipMessage || tooltipAge !== null) && (
        <div
          className="fixed z-50 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-xl max-w-sm pointer-events-none"
          style={{
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y - 50}px`,
            transform: "translateY(-100%)",
          }}
        >
          {tooltipAge !== null && (
            <div className="text-sm mb-2">
              <span className="font-semibold">👤 Возраст / Age:</span>{" "}
              <span className="text-blue-300">{tooltipAge}</span>
            </div>
          )}
          {tooltipMessage && (
            <>
              <div className="text-sm font-semibold mb-1">📜 Текст послания:</div>
              <div className="text-sm whitespace-pre-wrap break-words">
                {tooltipMessage}
              </div>
            </>
          )}
        </div>
      )}
      {/* Модальное окно выгрузки кодов */}
      <Modal
        isOpen={showDownloadModal}
        onClose={() => {
          setShowDownloadModal(false);
          setDownloadPostText("");
        }}
        title="Выгрузка кодов активации"
        size="lg"
      >
        <div className="space-y-4">
          {loadingUndownloaded ? (
            <div className="text-center py-8 text-gray-500">Загрузка...</div>
          ) : undownloadedCodes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Нет невыгруженных кодов
            </div>
          ) : (
            <>
              {/* Поле для пост-текста */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Пост-текст (опционально):
                </label>
                <input
                  type="text"
                  value={downloadPostText}
                  onChange={(e) => setDownloadPostText(e.target.value)}
                  placeholder="Введите текст для добавления после каждого кода"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Предпросмотр кодов */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Коды для выгрузки ({undownloadedCodes.length}):
                  </h3>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <pre className="text-sm font-mono whitespace-pre-wrap">
                    {undownloadedCodes
                      .map((code) => `${code.code}${downloadPostText ? ' ' + downloadPostText : ''}`)
                      .join('\n')}
                  </pre>
                </div>
              </div>

              {/* Кнопки действий */}
              <div className="flex gap-3">
                <button
                  onClick={handleCopyCodesWithPostText}
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all hover:scale-105"
                >
                  📋 Скопировать все
                </button>
                <button
                  onClick={handleMarkAsDownloaded}
                  className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all hover:scale-105"
                >
                  ✅ Выгружено
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};
